import { forwardRef } from 'react';

export interface OnboardingProgressBarProps {
  /** Progress value 0-100 */
  progress: number;
  className?: string;
}

/**
 * One continuous bar, in every onboarding flow.
 *
 * It used to also render a discrete segmented variant, which the invitee flow
 * opted into. That came from an acceptance criterion; Figma draws the bar
 * continuous in both flows, and the conflict sat unresolved for weeks. Product
 * settled it as continuous, so the segmented mode is gone rather than merely
 * unused — a prop nobody should pass is an invitation to pass it, and this
 * component is shared by both flows.
 */
export const OnboardingProgressBar = forwardRef<
  HTMLDivElement,
  OnboardingProgressBarProps
>(function OnboardingProgressBar({ progress, className }, ref) {
  const clampedProgress = Math.min(100, Math.max(0, progress));

  return (
    <div
      ref={ref}
      className={[
        'w-full h-1 bg-disabled-bg rounded-full overflow-hidden',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="progressbar"
      aria-label="Onboarding progress"
      aria-valuenow={clampedProgress}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full rounded-full bg-primary transition-all duration-300"
        style={{ width: `${clampedProgress}%` }}
      />
    </div>
  );
});
